import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

/**
 * Every query whose answer a change to one appointment can alter.
 *
 * Listed one by one because React Query matches a key by its leading *elements*, not by URL
 * prefix: `['/appointments']` refreshes `['/appointments', {...}]` and nothing else, so
 * `/appointments/requests/count` -- the badge in the header -- has to be named in its own
 * right or it would go on showing a request somebody has just answered.
 */
const DIARY_QUERY_ROOTS: readonly string[] = [
    '/appointments',
    '/appointments/history',
    '/appointments/requests',
    '/appointments/requests/count',
    '/appointments/empty-slots',
];

/**
 * Marks everything the diary draws as stale, so whatever is on screen refetches.
 *
 * A request is answered in one place and shown in several at once -- the strip above the
 * calendar, the card in the grid, the count in the header, the patient's own list -- and each
 * host used to refetch only what it had drawn itself. Confirming from the calendar card then
 * left the strip and the badge still asking for an answer already given.
 */
export const useRefreshDiary = (): (() => void) => {
    const queryClient = useQueryClient();

    return useCallback((): void => {
        DIARY_QUERY_ROOTS.forEach(root => {
            void queryClient.invalidateQueries({ queryKey: [root] });
        });
    }, [queryClient]);
};
